import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Grid,
  Divider,
  LinearProgress,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  History as HistoryIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { balanceService } from '../services/balanceService';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface BalanceData {
  current: {
    current_balance: number;
    total_income: number;
    total_expenses: number;
    monthly_income: number;
    monthly_expenses: number;
    last_updated: string;
  };
  account: {
    id: number;
    code: string;
    name: string;    
    minimum_balance: number;
    currency: {
        symbol: string;
        code: string;
    };
  };
  recent_transactions: Array<{
    id: number;
    transaction_type: string;
    amount: number;
    description: string;
    transaction_date: string;
    source_type: string;
    user: {
      name: string;
    };
  }>;
}

const BalanceWidget: React.FC<{ onRefresh?: () => void }> = ({ onRefresh }) => {
  const [data, setData] = useState<BalanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    loadBalance();
  }, []);

  const loadBalance = async () => {
    try {
      setLoading(true);
      const response = await balanceService.getCurrentBalance();
      setData(response);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar balance');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await balanceService.recalculate();
      await loadBalance();
      enqueueSnackbar('Balance actualizado', { variant: 'success' });
      if (onRefresh) onRefresh();
    } catch (err: any) {
      enqueueSnackbar('Error al actualizar balance', { variant: 'error' });
    } finally {
      setRefreshing(false);
    }
  };

  const getBalanceColor = (balance: number) => {
    if (!data) return 'info';
    if (balance < data.account.minimum_balance) return 'error';
    if (balance > 100000) return 'success';
    if (balance > 50000) return 'info';
    if (balance > 0) return 'warning';
    return 'error';
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'monthly_payment': return <PaymentIcon />;
      case 'expense': return <ReceiptIcon />;
      case 'donation': return <TrendingUpIcon />;
      default: return <HistoryIcon />;
    }
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'monthly_payment': return 'success';
      case 'donation': return 'info';
      case 'expense': return 'error';
      default: return 'default';
    }
  };

  const formatAmount = (amount: number, type: string) => {
    const isPositive = ['income', 'monthly_payment', 'donation', 'transfer_in'].includes(type);
    return (
      <Typography
        variant="body2"
        color={isPositive ? 'success.main' : 'error.main'}
        fontWeight="medium"
      >
        {isPositive ? '+' : '-'} ₽{Math.abs(amount).toLocaleString()}
      </Typography>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error || 'No hay datos de balance'}</Alert>
        </CardContent>
      </Card>
    );
  }

  const balanceColor = getBalanceColor(data.current.current_balance);

  return (
    <Card sx={{ position: 'relative', overflow: 'visible' }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center">
            <AccountBalanceIcon sx={{ fontSize: 40, color: `${balanceColor}.main`, mr: 2 }} />
            <Box>
              <Typography variant="h6" component="div">
                {data.account.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {data.account.code} • {data.account.currency?.symbol || data.account.currency?.code || 'RUB'}
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Actualizar balance">
            <IconButton onClick={handleRefresh} disabled={refreshing}>
              <RefreshIcon sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: `${balanceColor}.light`,
                color: `${balanceColor}.dark`,
                textAlign: 'center'
              }}
            >
              <Typography variant="h3" component="div" fontWeight="bold">
                ₽{data.current.current_balance.toLocaleString()}
              </Typography>
              <Typography variant="body2">
                {data.current.current_balance > data.account.minimum_balance 
                  ? 'Saldo Saludable' 
                  : 'Saldo Bajo'}
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 12}}>
            <Box textAlign="center" p={1}>
              <Typography variant="body2" color="text.secondary">
                Ingresos Totales
              </Typography>
              <Typography variant="h6" color="success.main">
                ₽{data.current.total_income.toLocaleString()}
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Box textAlign="center" p={1}>
              <Typography variant="body2" color="text.secondary">
                Gastos Totales
              </Typography>
              <Typography variant="h6" color="error.main">
                ₽{data.current.total_expenses.toLocaleString()}
              </Typography>
            </Box>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 1 }} />
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Box textAlign="center">
              <Typography variant="body2" color="text.secondary">
                Ingresos del Mes
              </Typography>
              <Box display="flex" alignItems="center" justifyContent="center">
                <TrendingUpIcon color="success" fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="body1" fontWeight="medium" color="success.main">
                  ₽{data.current.monthly_income.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 6 }}>
            <Box textAlign="center">
              <Typography variant="body2" color="text.secondary">
                Gastos del Mes
              </Typography>
              <Box display="flex" alignItems="center" justifyContent="center">
                <TrendingDownIcon color="error" fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="body1" fontWeight="medium" color="error.main">
                  ₽{data.current.monthly_expenses.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Transacciones Recientes */}
          {data.recent_transactions.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Transacciones Recientes
              </Typography>
              <List dense>
                {data.recent_transactions.map((tx) => (
                  <ListItem key={tx.id} divider>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: `${getTransactionColor(tx.transaction_type)}.light` }}>
                        {getTransactionIcon(tx.transaction_type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={tx.description}
                      secondary={
                        <>
                          {format(new Date(tx.transaction_date), "d 'de' MMMM", { locale: es })}
                          {tx.user && ` • por ${tx.user.name}`}
                        </>
                      }
                    />
                    {formatAmount(tx.amount, tx.transaction_type)}
                  </ListItem>
                ))}
              </List>
            </Grid>
          )}

          <Grid size={{ xs: 12 }}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              onClick={() => window.open('/balance/transactions', '_blank')}
              startIcon={<HistoryIcon />}
            >
              Ver Historial Completo
            </Button>
          </Grid>
        </Grid>

        <Box mt={2}>
          <Typography variant="caption" color="text.secondary">
            Última actualización: {format(new Date(data.current.last_updated), "dd/MM/yyyy HH:mm")}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default BalanceWidget;